import { Injectable, signal } from '@angular/core';

// ─── Public types ─────────────────────────────────────────────────────────────
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id:       number;
  type:     ToastType;
  title?:   string;
  message:  string;
  duration: number;   // ms until auto-dismiss
  leaving:  boolean;  // true while slide-out animation plays
}

/**
 * ToastService
 *
 * Injectable service for showing non-blocking toast notifications from anywhere.
 *
 * Usage:
 *   this.toastService.success('Saved successfully', 'Done');
 *   this.toastService.error('Could not reach server', 'Upload failed');
 *   this.toastService.warning('Disk usage above 80%');
 *   this.toastService.info('Session expires in 5 minutes');
 *
 * Toasts auto-dismiss after `duration` ms (default 4 500 ms).
 * Multiple toasts stack in the bottom-right corner.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {

  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private _nextId = 0;

  // ── Core show method ────────────────────────────────────────────────────────
  show(type: ToastType, message: string, title?: string, duration = 4500): void {
    const id = ++this._nextId;
    this._toasts.update(list => [
      ...list,
      { id, type, message, title, duration, leaving: false },
    ]);
    // Schedule auto-dismiss
    setTimeout(() => this.dismiss(id), duration);
  }

  // ── Convenience wrappers ────────────────────────────────────────────────────
  success(message: string, title?: string, duration?: number): void {
    this.show('success', message, title, duration);
  }

  error(message: string, title?: string, duration?: number): void {
    this.show('error', message, title, duration);
  }

  warning(message: string, title?: string, duration?: number): void {
    this.show('warning', message, title, duration);
  }

  info(message: string, title?: string, duration?: number): void {
    this.show('info', message, title, duration);
  }

  // ── Dismiss (with leave animation) ─────────────────────────────────────────
  dismiss(id: number): void {
    // Mark as leaving → CSS plays slide-out animation
    this._toasts.update(list =>
      list.map(t => t.id === id ? { ...t, leaving: true } : t)
    );
    // Remove from list after animation (300 ms)
    setTimeout(() => {
      this._toasts.update(list => list.filter(t => t.id !== id));
    }, 300);
  }
}
